import { isEmpty } from 'lodash-es'
import { action, computed, observable, runInAction } from 'mobx'
import { task } from 'mobx-task'
import { catchError, combineLatest, defer, EMPTY, from } from 'rxjs'
import { distinctUntilChanged, switchMap } from 'rxjs/operators'
import {
  InfiniteScrollProvider,
  type FetchItems,
} from '@/infra/InfiniteScrollProvider'
import { tapAction, toStream } from '@/utils/mobx-util'
import { withAbortSignal } from '@/utils/rx-util'
import { type FilterItem, type FilterViewModel } from '@/infra/FiltersViewModel'
import { SearchTextViewModel } from '@/infra/SearchTextViewModel'
import type { ActivationLifecycle } from '@/infra/activation'
import { LifecycleTracker } from '@/infra/lifecycle-tracker'

/**
 * Filter that selects one or more values from options loaded page by page
 * and searched on the server.
 */
export class InfiniteSelectFilter<TKey, TValue>
  implements FilterViewModel<TKey, TValue[]>, ActivationLifecycle
{
  private readonly _lifecycle = new LifecycleTracker()
  private readonly _searchText = new SearchTextViewModel()
  private readonly _fetchItems: FetchItems<FilterItem<TValue>>

  /** @inheritDoc */
  key: TKey

  /** @inheritDoc */
  title: string

  /** @inheritDoc */
  @observable accessor editing = false

  /**
   * Whether more than one value can be selected.
   */
  @observable accessor multi = false

  /**
   * Whether every option can be selected at once.
   */
  @observable accessor allowSelectAll = false

  /**
   * The default limit of items to display.
   */
  @observable accessor defaultLimit: number | undefined = undefined

  /**
   * The selected values.
   */
  @observable accessor selected: TValue[] = []

  /**
   * The options loaded so far, or restored from storage.
   */
  @observable accessor options: FilterItem<TValue>[] = []

  /**
   * Pages the options.
   */
  scrollProvider = new InfiniteScrollProvider<FilterItem<TValue>>({
    limit: 20,
    fetchItems: (options) => this._fetchItems(options),
  })

  /**
   * Loads the first page of options.
   */
  fetch = task(async () => {
    await this.scrollProvider.fetch({ signal: this._lifecycle.signal })
  })

  private readonly _searches$ = this._searchText.debouncedValue$.pipe(
    switchMap((searchText) =>
      withAbortSignal((signal) =>
        this.scrollProvider.fetch({ filter: { searchText }, signal }),
      ).pipe(
        // The options keep the last page, and the next search tries again.
        catchError(() => EMPTY),
      ),
    ),
  )

  private readonly _cursorUpdates$ = toStream(
    () => this.scrollProvider.cursor,
    true,
  ).pipe(
    distinctUntilChanged(),
    tapAction(() => {
      this.setOptions([...this.scrollProvider.items])
    }),
  )

  private readonly _combined$ = combineLatest([
    this._searches$,
    this._cursorUpdates$,
  ])

  constructor(
    key: TKey,
    title: string,
    fetchItems: FetchItems<FilterItem<TValue>>,
    multi?: boolean,
    allowSelectAll?: boolean,
    defaultLimit?: number,
  ) {
    this.key = key
    this.title = title
    this.multi = multi ?? false
    this.allowSelectAll = allowSelectAll ?? false
    this.defaultLimit = defaultLimit
    this.selected = []
    this.options = []
    this._fetchItems = fetchItems

    // Searching starts once the first page has loaded; `fetch` holds the error
    // when it fails.
    this._lifecycle.manage(
      defer(() => from(this.fetch())).pipe(
        catchError(() => EMPTY),
        switchMap(() => this._combined$),
      ),
    )
  }

  /** @inheritDoc */
  @computed
  get valid() {
    return !isEmpty(this.value)
  }

  /** @inheritDoc */
  @computed
  get value() {
    return this.selected
  }

  /**
   * The options loaded so far.
   */
  @computed
  get items() {
    return this.scrollProvider.items
  }

  /**
   * Option titles by value.
   */
  @computed
  get optionsMap() {
    return new Map(this.options.map((o) => [o.value, o.title]))
  }

  /** @inheritDoc */
  activate() {
    this._lifecycle.activate()
  }

  /** @inheritDoc */
  deactivate() {
    this._lifecycle.deactivate()
  }

  /** @inheritDoc */
  deserialize(value: unknown): void {
    try {
      if (typeof value === 'string') {
        this.options = JSON.parse(value) as FilterItem<TValue>[]
        this.selected = this.options.map((o) => o.value)
      }
    } catch {
      this.clearValue()
    }
  }

  /** @inheritDoc */
  serialize() {
    return JSON.stringify(
      this.options.filter((o) => this.selected.includes(o.value)),
    )
  }

  /** @inheritDoc */
  @computed
  get valueText() {
    return this.toString()
  }

  /**
   * Lists the selected titles, truncated to `limit` (or the default limit)
   * with a "+n selected" suffix.
   *
   * @param limit
   */
  toString(limit?: number) {
    let items = this.selected.map((v) => this.optionsMap.get(v))
    const max = limit ?? this.defaultLimit

    if (max && items.length > max) {
      items = items.slice(0, max - 1)

      const otherItemsCount = this.selected.length - items.length
      items.push(`${max > 1 ? '+' : ''}${otherItemsCount} selected`)
    }

    return items.join(', ')
  }

  /** @inheritDoc */
  @action.bound
  startEditing(): void {
    this.editing = true
  }

  /** @inheritDoc */
  @action.bound
  stopEditing(): void {
    this.editing = false
  }

  /**
   * Clears the value and closes the editor.
   */
  @action.bound
  clear() {
    this.clearValue()
    this.stopEditing()
  }

  /**
   * Replaces the options.
   *
   * @param options
   */
  @action.bound
  setOptions(options: FilterItem<TValue>[]) {
    this.options = options
  }

  /**
   * Searches the options.
   *
   * @param text
   */
  @action.bound
  changeSearchText(text: string) {
    this._searchText.change(text)
  }

  /**
   * Selects or deselects the value. A single-select filter only selects.
   *
   * @param val
   */
  @action.bound
  toggle(val: TValue) {
    if (this.multi) {
      this.change(
        this.isSelected(val)
          ? this.selected.filter((s) => s !== val)
          : [...this.selected, val],
      )
      return
    }

    if (!this.isSelected(val)) {
      this.selected = [val]
    }
  }

  /**
   * Checks whether the value is selected.
   *
   * @param val
   */
  isSelected(val: TValue) {
    return this.selected.some((s) => s === val)
  }

  /**
   * Replaces the selected values.
   *
   * @param val
   */
  @action.bound
  change(val: TValue[]) {
    this.selected = val
  }

  /** @inheritDoc */
  @action.bound
  clearValue(): void {
    this.selected = []
  }

  /**
   * Loads every page, then selects all options.
   */
  @action.bound
  async selectAll() {
    while (!this.scrollProvider.isDone) {
      await this.scrollProvider.more()
    }

    runInAction(() => {
      this.selected = this.items.map((x) => x.value)
    })
  }
}
